import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { getUser, grabRetentionPrize } from '@/shared/api/user/user.api';

import { type UserState, type UserStore } from './user.types';
import { useWalletStateRaw } from './wallet.store';
import { WalletType } from './wallet.types';

const defaultUserState: UserState = {
  user: undefined,
  retentionModalPrize: undefined,
  fight: undefined,
};

export const useUserStateRaw = create<UserStore>()((set) => ({
  ...defaultUserState,
  actions: {
    hydrate: (fresh) => set(fresh),
    fetchUser: async () => {
      try {
        const user = await getUser();

        const walletTypes = Object.values(WalletType);
        const filledMissingWallets = walletTypes.map((type) => {
          const existingWallet = user.wallets?.find((wallet) => wallet.type === type);
          return (
            existingWallet || {
              type,
              balance: 0,
              totalEarned: 0,
              minAmountToWithdraw: 1,
              data: null,
            }
          );
        });

        useWalletStateRaw.getState().actions.hydrate({ wallets: filledMissingWallets });

        set({ user: user });

        return user;
      } catch (err) {
        console.error('ERROR in fetchUser', err);
        return null;
      }
    },

    getRetentionPrize: async () => {
      try {
        const prize = await grabRetentionPrize();
        set({ retentionModalPrize: prize });

        return prize;
      } catch (err) {
        console.error('ERROR in getRetentionPrize', err);
      }
    },
  },
}));

export const useUserState = () =>
  useUserStateRaw(
    useShallow((state) => ({
      user: state.user,
      retentionModalPrize: state.retentionModalPrize,
    }))
  );

export const useUserActions = () => useUserStateRaw((state) => state.actions);

export const useUser = () => {
  return useUserStateRaw(useShallow((state) => state.user));
};
